/**
 * File Storage Service
 * 
 * Handles file uploads to Firebase Storage instead of storing in Firestore
 */

import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { storage } from '../config/firebase';
import { ImageCompressionService, CompressionOptions } from './ImageCompressionService';

export interface FileUploadResult {
  url: string;
  path: string;
  name: string;
  size: number;
  type: string;
}

export interface FileMetadata {
  url: string;
  path: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: Date;
  uploadedBy: string;
}

export interface UploadProgress {
  completedFiles: number;
  totalFiles: number;
  currentFileName: string;
  currentFileProgress: number; // 0-100
  overallProgress: number; // 0-100
}

export type FileUploadPhase = 'pending' | 'compressing' | 'uploading' | 'complete' | 'error' | 'retrying';

export interface FileUploadStatus {
  name: string;
  status: FileUploadPhase;
  progress: number; // 0-100 (byte-level during upload)
  error?: string;
  originalSize: number;
  compressedSize?: number;
}

export interface DetailedUploadProgress {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  overallProgress: number; // 0-100
  currentPhase: string;
  fileStatuses: FileUploadStatus[];
  isOnline: boolean;
  totalBytes: number;
  bytesTransferred: number;
}

export class FileStorageService {
  private static instance: FileStorageService;
  private imageCompressionService: ImageCompressionService;
  private readonly UPLOAD_TIMEOUT_MS = 180000; // 3 minutes per file (increased for large files)
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 1000; // Initial delay of 1 second

  static getInstance(): FileStorageService {
    if (!FileStorageService.instance) {
      FileStorageService.instance = new FileStorageService();
    }
    return FileStorageService.instance;
  }

  constructor() {
    this.imageCompressionService = ImageCompressionService.getInstance();
  }

  /**
   * Wraps a promise with a timeout
   */
  private async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    errorMessage: string
  ): Promise<T> {
    const timeoutPromise = new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`${errorMessage} (timeout after ${timeoutMs}ms)`));
      }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]);
  }

  /**
   * Run async tasks with a concurrency limit to avoid main-thread blocking and OOM
   * when uploading/compressing many images at once.
   */
  private async runWithConcurrencyLimit<T, R>(
    items: T[],
    concurrency: number,
    fn: (item: T, index: number) => Promise<R>
  ): Promise<PromiseSettledResult<R>[]> {
    if (items.length === 0) return [];
    const results: PromiseSettledResult<R>[] = new Array(items.length);
    let nextIndex = 0;
    const worker = async (): Promise<void> => {
      while (nextIndex < items.length) {
        const i = nextIndex++;
        const item = items[i];
        try {
          const value = await fn(item, i);
          results[i] = { status: 'fulfilled' as const, value };
        } catch (reason) {
          results[i] = { status: 'rejected' as const, reason };
        }
      }
    };
    const workers = Array.from(
      { length: Math.min(concurrency, items.length) },
      () => worker()
    );
    await Promise.all(workers);
    return results;
  }

  /**
   * Retry a function with exponential backoff
   */
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    retries: number = this.MAX_RETRIES,
    delay: number = this.RETRY_DELAY_MS
  ): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      if (retries <= 0) {
        throw error;
      }
      
      console.log(`Retrying upload (${this.MAX_RETRIES - retries + 1}/${this.MAX_RETRIES}) after ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return this.retryWithBackoff(fn, retries - 1, delay * 2); // Exponential backoff
    }
  }

  /**
   * Upload a file to Firebase Storage with timeout and retry
   */
  async uploadFile(
    file: File, 
    path: string, 
    uploadedBy: string
  ): Promise<FileUploadResult> {
    const uploadOperation = async () => {
      try {
        // Create a unique filename to prevent conflicts
        const timestamp = Date.now();
        const uniqueFilename = `${timestamp}_${file.name}`;
        const fullPath = `${path}/${uniqueFilename}`;
        
        // Create storage reference
        const storageRef = ref(storage, fullPath);
        
        // Upload file with timeout
        const uploadPromise = uploadBytes(storageRef, file);
        const snapshot = await this.withTimeout(
          uploadPromise,
          this.UPLOAD_TIMEOUT_MS,
          `Upload timeout for ${file.name}`
        );
        
        // Get download URL with timeout
        const urlPromise = getDownloadURL(snapshot.ref);
        const downloadURL = await this.withTimeout(
          urlPromise,
          30000, // 30 seconds for URL retrieval
          `Failed to get download URL for ${file.name}`
        );
        
        return {
          url: downloadURL,
          path: fullPath,
          name: file.name,
          size: file.size,
          type: file.type
        };
      } catch (error: any) {
        console.error(`Failed to upload file ${file.name}:`, error);
        throw new Error(`Failed to upload file "${file.name}": ${error.message || error}`);
      }
    };

    // Retry with exponential backoff
    return this.retryWithBackoff(uploadOperation);
  }

  /**
   * Upload a file with progress tracking using resumable upload
   * This provides better feedback for large files and is more reliable
   */
  async uploadFileWithProgress(
    file: File | Blob,
    path: string,
    fileName: string,
    onProgress?: (progress: number) => void
  ): Promise<FileUploadResult> {
    return new Promise((resolve, reject) => {
      try {
        const timestamp = Date.now();
        const uniqueFilename = `${timestamp}_${fileName}`;
        const fullPath = `${path}/${uniqueFilename}`;
        
        const storageRef = ref(storage, fullPath);
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        // Set up timeout
        const timeoutId = setTimeout(() => {
          uploadTask.cancel();
          reject(new Error(`Upload timeout for ${fileName}`));
        }, this.UPLOAD_TIMEOUT_MS);
        
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            if (onProgress) {
              onProgress(progress);
            }
          },
          (error) => {
            clearTimeout(timeoutId);
            console.error(`Upload error for ${fileName}:`, error);
            reject(new Error(`Failed to upload "${fileName}": ${error.message}`));
          },
          async () => {
            clearTimeout(timeoutId);
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                path: fullPath,
                name: fileName,
                size: file.size,
                type: file instanceof File ? file.type : 'application/octet-stream'
              });
            } catch (urlError: any) {
              reject(new Error(`Failed to get download URL for ${fileName}: ${urlError.message}`));
            }
          }
        );
      } catch (error: any) {
        reject(new Error(`Failed to start upload for ${fileName}: ${error.message}`));
      }
    });
  }

  /**
   * Get aggressive compression options for very large files (>5MB)
   * Reduces dimensions and quality more to ensure successful upload
   */
  private getAggressiveCompressionOptions(file: File): CompressionOptions {
    const fileSizeMB = file.size / (1024 * 1024);
    
    if (fileSizeMB > 8) {
      // Very large files (>8MB): Maximum compression
      return {
        maxWidth: 1200,
        maxHeight: 900,
        quality: 0.6,
        format: 'jpeg'
      };
    } else if (fileSizeMB > 5) {
      // Large files (5-8MB): High compression
      return {
        maxWidth: 1400,
        maxHeight: 1050,
        quality: 0.7,
        format: 'jpeg'
      };
    } else if (fileSizeMB > 3) {
      // Medium-large files (3-5MB): Medium compression
      return {
        maxWidth: 1600,
        maxHeight: 1200,
        quality: 0.75,
        format: 'jpeg'
      };
    }
    
    // Default compression
    return {
      maxWidth: 1920,
      maxHeight: 1080,
      quality: 0.8,
      format: 'jpeg'
    };
  }

  /**
   * Upload a file with image compression (for images only)
   * For large files (>5MB), uses aggressive compression to ensure successful upload
   */
  async uploadFileWithCompression(
    file: File, 
    path: string, 
    uploadedBy: string,
    compressionOptions?: CompressionOptions,
    onProgress?: (progress: number) => void
  ): Promise<FileUploadResult> {
    const uploadOperation = async () => {
      try {
        // Check if file is an image that can be compressed
        if (this.imageCompressionService.canCompress(file)) {
          const fileSizeMB = file.size / (1024 * 1024);
          console.log(`📷 Compressing image: ${file.name} (${fileSizeMB.toFixed(2)} MB)`);
          
          // For large files, use aggressive compression to ensure successful upload
          const options = compressionOptions || 
            (fileSizeMB > 3 ? this.getAggressiveCompressionOptions(file) : this.imageCompressionService.getCompressionRecommendations(file));
          
          console.log(`📷 Using compression: ${options.maxWidth}x${options.maxHeight}, quality: ${options.quality}`);
          
          // Compress the image with longer timeout for large files
          const compressionTimeoutMs = this.isMobileDevice() ? 45000 : 90000;
          const compressionPromise = this.imageCompressionService.compressImage(file, options);
          const compressionResult = await this.withTimeout(
            compressionPromise,
            compressionTimeoutMs,
            `Compression timeout for ${file.name}`
          );
          
          const compressedSizeMB = compressionResult.compressedSize / (1024 * 1024);
          console.log(`📷 Compressed ${file.name}: ${fileSizeMB.toFixed(2)} MB → ${compressedSizeMB.toFixed(2)} MB (${compressionResult.compressionRatio}% reduction)`);
          
          // Extension must match the actual blob MIME (original may be kept when
          // compression expands the file — don't label a JPEG as .png).
          const blobType = (compressionResult.blob.type || file.type || '').toLowerCase();
          const fileExtension = blobType.includes('png')
            ? 'png'
            : blobType.includes('webp')
              ? 'webp'
              : blobType.includes('gif')
                ? 'gif'
                : 'jpeg';
          const baseName = file.name.replace(/\.[^/.]+$/, '');
          const wasReencoded = compressionResult.compressedSize !== file.size || compressionResult.blob !== file;
          const compressedFilename = wasReencoded
            ? `${baseName}_compressed.${fileExtension}`
            : file.name;
          
          // Use resumable upload with progress tracking for better reliability
          const result = await this.uploadFileWithProgress(
            compressionResult.blob,
            path,
            compressedFilename,
            onProgress
          );
          
          console.log(`✅ Image compressed and uploaded: ${compressionResult.compressionRatio}% size reduction`);
          
          return {
            ...result,
            size: compressionResult.compressedSize,
            type: compressionResult.blob.type
          };
        } else {
          // Not an image or can't be compressed, upload as-is with progress
          console.log(`📄 Uploading non-image file: ${file.name}`);
          return this.uploadFileWithProgress(file, path, file.name, onProgress);
        }
      } catch (error: any) {
        console.error(`Failed to upload file with compression ${file.name}:`, error);
        // Fallback to regular upload if compression fails
        console.log('🔄 Falling back to regular upload...');
        return this.uploadFileWithProgress(file, path, file.name, onProgress);
      }
    };

    // Retry with exponential backoff
    return this.retryWithBackoff(uploadOperation);
  }

  /**
   * Upload multiple files to Firebase Storage
   */
  async uploadMultipleFiles(
    files: File[], 
    path: string, 
    uploadedBy: string
  ): Promise<FileUploadResult[]> {
    try {
      const uploadPromises = files.map(file => 
        this.uploadFile(file, path, uploadedBy)
      );
      
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error: any) {
      console.error('Failed to upload multiple files:', error);
      throw new Error(`Failed to upload files: ${error?.message || error}`);
    }
  }

  /**
   * Delete a file from Firebase Storage
   */
  async deleteFile(path: string): Promise<void> {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      console.log('File deleted successfully:', path);
    } catch (error: any) {
      console.error('Failed to delete file:', error);
      throw new Error(`Failed to delete file: ${error?.message || error}`);
    }
  }

  /**
   * Get all files in a directory
   */
  async listFiles(path: string): Promise<FileMetadata[]> {
    try {
      const storageRef = ref(storage, path);
      const result = await listAll(storageRef);
      
      const filePromises = result.items.map(async (item) => {
        const downloadURL = await getDownloadURL(item);
        return {
          url: downloadURL,
          path: item.fullPath,
          name: item.name,
          size: 0, // Size not available from listAll
          type: '', // Type not available from listAll
          uploadedAt: new Date(),
          uploadedBy: 'unknown'
        };
      });
      
      return Promise.all(filePromises);
    } catch (error: any) {
      console.error('Failed to list files:', error);
      throw new Error(`Failed to list files: ${error?.message || error}`);
    }
  }

  /**
   * Generate storage path for store notes
   */
  generateStoreNotesPath(storeId: string, noteId: string): string {
    return `store-notes/${storeId}/${noteId}`;
  }

  /**
   * Generate storage path for store media
   */
  generateStoreMediaPath(storeId: string, noteId: string): string {
    return `store-media/${storeId}/${noteId}`;
  }

  /**
   * Generate storage path for store documents
   */
  generateStoreDocumentsPath(storeId: string, noteId: string): string {
    return `store-documents/${storeId}/${noteId}`;
  }

  /**
   * Generate storage path for competitor display photos
   */
  generateCompetitorPhotosPath(storeId: string, noteId: string, competitorId: string): string {
    return `store-media/${storeId}/${noteId}/competitors/${competitorId}`;
  }

  /**
   * Detect if running on a mobile device (helps avoid canvas/memory issues on Samsung Galaxy, etc.)
   */
  private isMobileDevice(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Upload store note attachments with image compression
   * Uses Promise.allSettled to handle individual file failures gracefully.
   * On mobile, media files are uploaded sequentially to prevent main-thread blocking and OOM.
   */
  async uploadStoreNoteAttachments(
    storeId: string,
    noteId: string,
    mediaFiles: File[],
    documents: File[],
    uploadedBy: string,
    enableCompression: boolean = true
  ): Promise<{
    media: FileUploadResult[];
    documents: FileUploadResult[];
    errors?: { fileName: string; error: string }[];
  }> {
    try {
      const mediaPath = this.generateStoreMediaPath(storeId, noteId);
      const documentsPath = this.generateStoreDocumentsPath(storeId, noteId);
      const errors: { fileName: string; error: string }[] = [];
      const isMobile = this.isMobileDevice();

      // On mobile (e.g. Samsung Galaxy), upload media files ONE AT A TIME to avoid
      // blocking the main thread with parallel canvas compression and to reduce memory use.
      let mediaSettled: PromiseSettledResult<FileUploadResult>[];
      if (mediaFiles && mediaFiles.length > 0) {
        if (isMobile && mediaFiles.length > 1) {
          const results: PromiseSettledResult<FileUploadResult>[] = [];
          for (let i = 0; i < mediaFiles.length; i++) {
            const file = mediaFiles[i];
            try {
              const result = enableCompression
                ? await this.uploadFileWithCompression(file, mediaPath, uploadedBy)
                : await this.uploadFile(file, mediaPath, uploadedBy);
              results.push({ status: 'fulfilled' as const, value: result });
            } catch (error: any) {
              errors.push({ fileName: file.name, error: error.message || String(error) });
              results.push({ status: 'rejected' as const, reason: error });
            }
          }
          mediaSettled = results;
        } else {
          // Desktop: limit concurrency (e.g. 3) so many images don't freeze the page
          // or cause OOM from parallel canvas compression
          const MAX_CONCURRENT_MEDIA = 3;
          mediaSettled = await this.runWithConcurrencyLimit(
            mediaFiles,
            MAX_CONCURRENT_MEDIA,
            async (file) => {
              try {
                return enableCompression
                  ? await this.uploadFileWithCompression(file, mediaPath, uploadedBy)
                  : await this.uploadFile(file, mediaPath, uploadedBy);
              } catch (error: any) {
                errors.push({ fileName: file.name, error: error.message || String(error) });
                throw error;
              }
            }
          );
        }
      } else {
        mediaSettled = [];
      }

      // Upload documents without compression (can stay parallel; no canvas involved)
      const documentSettled =
        documents && documents.length > 0
          ? await Promise.allSettled(
              documents.map(async (file) => {
                try {
                  return await this.uploadFile(file, documentsPath, uploadedBy);
                } catch (error: any) {
                  errors.push({ fileName: file.name, error: error.message || String(error) });
                  throw error;
                }
              })
            )
          : [];

      // Extract successful results
      const mediaResults: FileUploadResult[] = [];
      const documentResults: FileUploadResult[] = [];

      mediaSettled.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          mediaResults.push(result.value);
        } else {
          const fileName = mediaFiles[index]?.name || 'unknown';
          console.error(`❌ Failed to upload media file ${fileName}:`, result.reason);
          if (!errors.some(e => e.fileName === fileName)) {
            errors.push({ 
              fileName, 
              error: result.reason?.message || String(result.reason) 
            });
          }
        }
      });

      (documentSettled as PromiseSettledResult<FileUploadResult>[]).forEach((result, index) => {
        if (result.status === 'fulfilled') {
          documentResults.push(result.value);
        } else {
          const fileName = documents[index]?.name || 'unknown';
          console.error(`❌ Failed to upload document ${fileName}:`, result.reason);
          if (!errors.some(e => e.fileName === fileName)) {
            errors.push({ 
              fileName, 
              error: result.reason?.message || String(result.reason) 
            });
          }
        }
      });

      // Log compression statistics
      if (enableCompression && mediaFiles && mediaFiles.length > 0 && mediaResults.length > 0) {
        const totalOriginalSize = mediaFiles.reduce((sum, file) => sum + file.size, 0);
        const totalCompressedSize = mediaResults.reduce((sum, result) => sum + result.size, 0);
        const totalReduction = Math.round((1 - totalCompressedSize / totalOriginalSize) * 100);
        
        console.log(`📷 Store note media compression summary:`);
        console.log(`   Files: ${mediaResults.length}/${mediaFiles.length} uploaded successfully`);
        console.log(`   Original size: ${this.formatFileSize(totalOriginalSize)}`);
        console.log(`   Compressed size: ${this.formatFileSize(totalCompressedSize)}`);
        console.log(`   Total reduction: ${totalReduction}%`);
      }

      // If all files failed, throw an error
      if (mediaResults.length === 0 && documentResults.length === 0 && errors.length > 0) {
        const errorMessage = `All file uploads failed. Errors: ${errors.map(e => `${e.fileName}: ${e.error}`).join('; ')}`;
        throw new Error(errorMessage);
      }

      // If some files failed, log warnings but return successful uploads
      if (errors.length > 0) {
        console.warn(`⚠️ Some files failed to upload (${errors.length} error(s)):`, errors);
      }

      const result: {
        media: FileUploadResult[];
        documents: FileUploadResult[];
        errors?: { fileName: string; error: string }[];
      } = {
        media: mediaResults,
        documents: documentResults
      };

      if (errors.length > 0) {
        result.errors = errors;
      }

      return result;
    } catch (error: any) {
      console.error('Failed to upload store note attachments:', error);
      throw error;
    }
  }

  /**
   * Upload competitor display photos with image compression
   * Uses Promise.allSettled to handle individual file failures gracefully
   */
  async uploadCompetitorPhotos(
    storeId: string,
    noteId: string,
    competitorId: string,
    photos: File[],
    uploadedBy: string,
    enableCompression: boolean = true
  ): Promise<FileUploadResult[]> {
    try {
      if (photos.length === 0) {
        return [];
      }

      const competitorPath = this.generateCompetitorPhotosPath(storeId, noteId, competitorId);

      // Upload photos with limited concurrency to avoid freezing with many images
      const MAX_CONCURRENT_PHOTOS = 3;
      const settledResults = await this.runWithConcurrencyLimit(
        photos,
        MAX_CONCURRENT_PHOTOS,
        async (file) => {
          try {
            return enableCompression
              ? await this.uploadFileWithCompression(file, competitorPath, uploadedBy)
              : await this.uploadFile(file, competitorPath, uploadedBy);
          } catch (error: any) {
            console.error(`❌ Failed to upload competitor photo ${file.name}:`, error);
            throw error;
          }
        }
      );

      const results: FileUploadResult[] = [];
      settledResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          results.push(result.value);
        } else {
          const fileName = photos[index]?.name || 'unknown';
          console.error(`❌ Failed to upload competitor photo ${fileName}:`, result.reason);
        }
      });

      // Log compression statistics
      if (enableCompression && photos.length > 0 && results.length > 0) {
        const totalOriginalSize = photos.reduce((sum, file) => sum + file.size, 0);
        const totalCompressedSize = results.reduce((sum, result) => sum + result.size, 0);
        const totalReduction = Math.round((1 - totalCompressedSize / totalOriginalSize) * 100);
        
        console.log(`📷 Competitor photos compression summary (${competitorId}):`);
        console.log(`   Files: ${results.length}/${photos.length} uploaded successfully`);
        console.log(`   Original size: ${this.formatFileSize(totalOriginalSize)}`);
        console.log(`   Compressed size: ${this.formatFileSize(totalCompressedSize)}`);
        console.log(`   Total reduction: ${totalReduction}%`);
      }

      // Return successful uploads even if some failed
      return results;
    } catch (error) {
      console.error('Failed to upload competitor photos:', error);
      throw error;
    }
  }

  /**
   * Upload ALL note files (store media, documents, AND competitor photos) in ONE efficient batch.
   * Tracks per-file progress with phases (compressing → uploading → complete/error).
   * Ensures the note can be saved even if some uploads fail.
   */
  async uploadAllNoteFiles(
    storeId: string,
    noteId: string,
    mediaFiles: File[],
    documents: File[],
    competitorData: { id: string; name: string; photos: File[]; stockLevel: number; displayPanels?: number }[],
    uploadedBy: string,
    enableCompression: boolean = true,
    onProgress?: (completed: number, total: number) => void,
    onDetailedProgress?: (progress: DetailedUploadProgress) => void
  ): Promise<{
    media: FileUploadResult[];
    documents: FileUploadResult[];
    competitorPhotos: { [competitorId: string]: FileUploadResult[] };
    errors: { fileName: string; error: string; source: string }[];
  }> {
    const errors: { fileName: string; error: string; source: string }[] = [];
    const mediaPath = this.generateStoreMediaPath(storeId, noteId);
    const documentsPath = this.generateStoreDocumentsPath(storeId, noteId);

    type UploadTask = {
      file: File;
      path: string;
      compress: boolean;
      source: 'media' | 'document' | string;
    };

    const tasks: UploadTask[] = [];

    for (const file of mediaFiles) {
      tasks.push({ file, path: mediaPath, compress: enableCompression, source: 'media' });
    }
    for (const file of documents) {
      tasks.push({ file, path: documentsPath, compress: false, source: 'document' });
    }
    for (const competitor of competitorData) {
      const competitorPath = this.generateCompetitorPhotosPath(storeId, noteId, competitor.id);
      for (const photo of competitor.photos) {
        tasks.push({ file: photo, path: competitorPath, compress: enableCompression, source: competitor.id });
      }
    }

    if (tasks.length === 0) {
      return { media: [], documents: [], competitorPhotos: {}, errors: [] };
    }

    // Check network before starting
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new Error('No internet connection. Please check your network and try again.');
    }

    console.log(`📤 Starting batch upload of ${tasks.length} files (media: ${mediaFiles.length}, docs: ${documents.length}, competitor photos: ${tasks.length - mediaFiles.length - documents.length})`);

    const isMobile = this.isMobileDevice();
    const MAX_CONCURRENT = isMobile ? 3 : 5;

    // Per-file status tracking
    const fileStatuses: FileUploadStatus[] = tasks.map(t => ({
      name: t.file.name,
      status: 'pending' as FileUploadPhase,
      progress: 0,
      originalSize: t.file.size,
    }));

    const totalBytes = tasks.reduce((sum, t) => sum + t.file.size, 0);
    let bytesUploaded = 0;

    const emitDetailedProgress = () => {
      if (!onDetailedProgress) return;
      const completed = fileStatuses.filter(f => f.status === 'complete').length;
      const failed = fileStatuses.filter(f => f.status === 'error').length;
      const activeFile = fileStatuses.find(f =>
        f.status === 'compressing' || f.status === 'uploading' || f.status === 'retrying'
      );
      const overallProgress = tasks.length > 0
        ? Math.round(((completed + failed) / tasks.length) * 100)
        : 0;

      onDetailedProgress({
        totalFiles: tasks.length,
        completedFiles: completed,
        failedFiles: failed,
        overallProgress,
        currentPhase: activeFile
          ? `${activeFile.status === 'compressing' ? 'Compressing' : activeFile.status === 'retrying' ? 'Retrying' : 'Uploading'} ${activeFile.name}...`
          : completed === tasks.length ? 'All files uploaded' : '',
        fileStatuses: [...fileStatuses],
        isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
        totalBytes,
        bytesTransferred: bytesUploaded,
      });
    };

    emitDetailedProgress();

    let completedCount = 0;
    const totalCount = tasks.length;

    const settledResults = await this.runWithConcurrencyLimit(
      tasks,
      MAX_CONCURRENT,
      async (task, index) => {
        const processFile = async (): Promise<{ result: FileUploadResult; source: string }> => {
          let fileToUpload: File | Blob = task.file;
          let fileName = task.file.name;

          // --- Compression phase ---
          if (task.compress && this.imageCompressionService.canCompress(task.file)) {
            fileStatuses[index].status = 'compressing';
            emitDetailedProgress();

            try {
              const fileSizeMB = task.file.size / (1024 * 1024);
              const options = fileSizeMB > 3
                ? this.getAggressiveCompressionOptions(task.file)
                : this.imageCompressionService.getCompressionRecommendations(task.file);

              const compressionTimeoutMs = isMobile ? 45000 : 90000;
              const compressionResult = await this.withTimeout(
                this.imageCompressionService.compressImage(task.file, options),
                compressionTimeoutMs,
                `Compression timeout for ${task.file.name}`
              );

              fileToUpload = compressionResult.blob;
              fileStatuses[index].compressedSize = compressionResult.compressedSize;
              const ext = options.format || 'jpeg';
              const baseName = task.file.name.replace(/\.[^/.]+$/, '');
              fileName = `${baseName}_compressed.${ext}`;
            } catch (compressError) {
              console.warn(`Compression failed for ${task.file.name}, uploading original:`, compressError);
            }
          }

          // --- Upload phase ---
          fileStatuses[index].status = 'uploading';
          fileStatuses[index].progress = 0;
          emitDetailedProgress();

          const result = await this.uploadFileWithProgress(
            fileToUpload,
            task.path,
            fileName,
            (pct) => {
              fileStatuses[index].progress = pct;
              emitDetailedProgress();
            }
          );

          // --- Complete ---
          const uploadedSize = fileStatuses[index].compressedSize || task.file.size;
          bytesUploaded += uploadedSize;
          fileStatuses[index].status = 'complete';
          fileStatuses[index].progress = 100;
          completedCount++;
          if (onProgress) onProgress(completedCount, totalCount);
          emitDetailedProgress();

          return { result, source: task.source };
        };

        // Retry with backoff + status tracking
        let lastError: any;
        for (let attempt = 0; attempt <= this.MAX_RETRIES; attempt++) {
          if (attempt > 0) {
            fileStatuses[index].status = 'retrying';
            fileStatuses[index].progress = 0;
            emitDetailedProgress();
            const delay = this.RETRY_DELAY_MS * Math.pow(2, attempt - 1);
            const jitter = Math.random() * 500;
            console.log(`🔄 Retrying ${task.file.name} (attempt ${attempt}/${this.MAX_RETRIES}) after ${Math.round(delay + jitter)}ms`);
            await new Promise(r => setTimeout(r, delay + jitter));
          }

          try {
            return await processFile();
          } catch (err: any) {
            lastError = err;
            if (attempt === this.MAX_RETRIES) {
              fileStatuses[index].status = 'error';
              fileStatuses[index].error = err?.message || 'Upload failed';
              completedCount++;
              if (onProgress) onProgress(completedCount, totalCount);
              emitDetailedProgress();
              errors.push({
                fileName: task.file.name,
                error: err?.message || String(err),
                source: task.source,
              });
              throw err;
            }
          }
        }
        throw lastError;
      }
    );

    // Organize results by source
    const mediaResults: FileUploadResult[] = [];
    const documentResults: FileUploadResult[] = [];
    const competitorPhotos: { [competitorId: string]: FileUploadResult[] } = {};

    for (const competitor of competitorData) {
      competitorPhotos[competitor.id] = [];
    }

    settledResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const { result: uploadResult, source } = result.value;
        if (source === 'media') {
          mediaResults.push(uploadResult);
        } else if (source === 'document') {
          documentResults.push(uploadResult);
        } else {
          if (!competitorPhotos[source]) {
            competitorPhotos[source] = [];
          }
          competitorPhotos[source].push(uploadResult);
        }
      }
    });

    const successCount = mediaResults.length + documentResults.length + 
      Object.values(competitorPhotos).reduce((sum, arr) => sum + arr.length, 0);
    console.log(`✅ Batch upload complete: ${successCount}/${tasks.length} files uploaded successfully`);
    if (errors.length > 0) {
      console.warn(`⚠️ ${errors.length} file(s) failed to upload:`, errors);
    }

    return { media: mediaResults, documents: documentResults, competitorPhotos, errors };
  }

  /**
   * Delete store note attachments
   */
  async deleteStoreNoteAttachments(
    storeId: string,
    noteId: string,
    mediaFiles: string[],
    documents: string[]
  ): Promise<void> {
    try {
      const deletePromises = [
        ...mediaFiles.map(path => this.deleteFile(path)),
        ...documents.map(path => this.deleteFile(path))
      ];

      await Promise.all(deletePromises);
      console.log('Store note attachments deleted successfully');
    } catch (error) {
      console.error('Failed to delete store note attachments:', error);
      throw error;
    }
  }

  /**
   * Get file size in human readable format
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File, maxSizeMB: number = 10): { valid: boolean; error?: string } {
    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `File size must be less than ${maxSizeMB}MB`
      };
    }

    // Check file type for images
    if (file.type.startsWith('image/')) {
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedImageTypes.includes(file.type)) {
        return {
          valid: false,
          error: 'Invalid image type. Allowed: JPEG, PNG, GIF, WebP'
        };
      }
    }

    // Check file type for documents
    if (!file.type.startsWith('image/')) {
      const allowedDocTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain'
      ];
      
      const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];
      const hasValidExtension = allowedExtensions.some(ext => 
        file.name.toLowerCase().endsWith(ext)
      );
      
      if (!hasValidExtension) {
        return {
          valid: false,
          error: 'Invalid document type. Allowed: PDF, DOC, DOCX, XLS, XLSX, TXT'
        };
      }
    }

    return { valid: true };
  }
}
